/**
 * useArtifactDownload Hook
 *
 * Hook para download de artefatos com estado de loading
 * - Download direto no formato nativo (sem dropdown)
 * - Feedback visual durante download
 * - Tratamento de erros
 */

import { useState, useCallback } from 'react';
import { downloadArtifact, getNativeFormat, type Artifact, type DownloadResult } from '../utils';
import { ERROR_MESSAGES } from '../constants';

// =============================================================================
// TYPES
// =============================================================================

export interface UseArtifactDownloadState {
  isDownloading: boolean;
  error: string | null;
  success: boolean;
}

export interface UseArtifactDownloadReturn extends UseArtifactDownloadState {
  download: (artifact: Artifact) => Promise<DownloadResult>;
  clearError: () => void;
  clearSuccess: () => void;
  getDownloadFormat: (artifactType: string) => string;
}

// =============================================================================
// HOOK
// =============================================================================

export const useArtifactDownload = (): UseArtifactDownloadReturn => {
  const [state, setState] = useState<UseArtifactDownloadState>({
    isDownloading: false,
    error: null,
    success: false,
  });

  /**
   * Executa download do artefato
   */
  const download = useCallback(async (artifact: Artifact): Promise<DownloadResult> => {
    setState({ isDownloading: true, error: null, success: false });

    try {
      const result = await downloadArtifact(artifact);

      if (result.success) {
        setState({ isDownloading: false, error: null, success: true });

        // Limpa estado de sucesso após 2s
        setTimeout(() => {
          setState((prev) => ({ ...prev, success: false }));
        }, 2000);
      } else {
        setState({
          isDownloading: false,
          error: result.error || ERROR_MESSAGES.DOWNLOAD_FAILED,
          success: false,
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : ERROR_MESSAGES.DOWNLOAD_FAILED;

      setState({
        isDownloading: false,
        error: errorMessage,
        success: false,
      });

      return { success: false, error: errorMessage };
    }
  }, []);

  /**
   * Limpa estado de erro
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Limpa estado de sucesso
   */
  const clearSuccess = useCallback(() => {
    setState((prev) => ({ ...prev, success: false }));
  }, []);

  /**
   * Retorna o formato de download para exibição
   */
  const getDownloadFormat = useCallback((artifactType: string): string => {
    return getNativeFormat(artifactType).toUpperCase();
  }, []);

  return {
    ...state,
    download,
    clearError,
    clearSuccess,
    getDownloadFormat,
  };
};

export default useArtifactDownload;
